import React from 'react'
import FileCard from './FileCard'
import { useDispatch, useSelector } from 'react-redux'
import { submissionActions } from '../../../../store/slices/submission.slice'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import { CheckCircleFilled, WarningOutlined } from '@ant-design/icons'
import { useThemeColors } from '../../../../hooks/useThemeColors'
import { Badge } from 'antd'
import { useParams } from 'react-router'
import { useVersionId } from '../../../../utils/parseURL.helper'
import SubmissionApproveStatuses from '../../../../constants/submission-approve-statuses'

const FileCardNewTextContainer = ({ id, name, badgeProps: customBadgeProps, approveStatus, mismatchPredicted, groups }) => {
    const colors = useThemeColors()
    const isOpened = useSelector(submissionSelectors.isOpened(id))
    const dispatch = useDispatch()
    const { token, layerId } = useParams()
    const versionId = useVersionId()

    const handleSubmissionClick = () => {
        dispatch(
            submissionActions.openSubmission({
                id,
                groups,
                token,
                versionId,
                questionRubricId: isNaN(Number(layerId)) ? undefined : Number(layerId),
            }),
        )
    }

    const getOverlayIcon = () => {
        if (!approveStatus) return null
        
        const approveStatusToTitle = {
            [SubmissionApproveStatuses.APPROVED]: 'Approved',
            [SubmissionApproveStatuses.AUTO_APPROVED]: 'Auto-approved',
            [SubmissionApproveStatuses.MISMATCH_APPROVED]: 'Mismatch approved',
            [SubmissionApproveStatuses.MISMATCH_CHANGED]: 'Mismatch changed',
        }

        return (
            <CheckCircleFilled
                style={{ color: 'white' }}
                title={approveStatusToTitle[approveStatus]}
            />
        )
    }

    const getOverlayBackgroundColor = () => {
        if (!approveStatus) return null

        const approveStatusToColor = {
            [SubmissionApproveStatuses.APPROVED]: colors.success,
            [SubmissionApproveStatuses.AUTO_APPROVED]: colors.success,
            [SubmissionApproveStatuses.MISMATCH_APPROVED]: colors.success,
            [SubmissionApproveStatuses.MISMATCH_CHANGED]: colors.warning,
        }

        return approveStatusToColor[approveStatus]
    }

    const buildBadgePropsArray = () => {
        const badgePropsArray = []
        
        // Add custom badge props if provided
        if (customBadgeProps) {
            badgePropsArray.push(customBadgeProps)
        }
        
        // Always show mismatch warning as a badge (not as overlay)
        if (mismatchPredicted) {
            badgePropsArray.push({
                count: <WarningOutlined style={{ color: colors.warning }} title='Mismatch Predicted' />,
            })
        }
        
        // Don't add approve status as badge since we're using overlay icon instead
        return badgePropsArray
    }

    const badgePropsArray = buildBadgePropsArray()
    const nBadges = badgePropsArray.length
    const overlayIcon = getOverlayIcon()
    const overlayBackgroundColor = getOverlayBackgroundColor()

    return badgePropsArray.reduceRight(
        (children, badgeProps, index) => {
            const offsetX = -(nBadges - index - 1) * 12
            return (
                <Badge key={index} {...badgeProps} offset={[offsetX, 0]}>
                    {children}
                </Badge>
            )
        },
        <FileCard 
            id={id} 
            name={name} 
            onClick={handleSubmissionClick} 
            isOpened={isOpened} 
            overlayIcon={overlayIcon}
            overlayBackgroundColor={overlayBackgroundColor}
            dataTest={mismatchPredicted ? 'submission-card-mismatch' : undefined}
        />,
    )
}

export default FileCardNewTextContainer
